'use client';

import {
  ForwardRefRenderFunction,
  HTMLAttributes,
  ReactNode,
  useImperativeHandle,
} from 'react';
import { Box, BoxProps, forwardRef } from '@chakra-ui/react';
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone';

interface ChildrenFunctionParams {
  isDragActive: boolean;
  openFileSelection(): void;
}

type ChildrenFunction = (params: ChildrenFunctionParams) => ReactNode;

interface Ref {
  openFileSelection(): void;
  openCamera(): void;
}

interface Props extends Omit<BoxProps, 'children'> {
  children?: ReactNode | ChildrenFunction;
  onSelectFile?(
    file: File[],
    rejectedFiles: FileRejection[],
    event: DropEvent,
  ): void;
  accept?: Record<string, string[]>;
  maxFiles?: number;
  preventClick?: boolean;
  whileDragActive?: BoxProps;
  isDisabled?: boolean;
  inputProps?: HTMLAttributes<HTMLInputElement> & {
    'data-testid': string;
  };
}

export type DropzoneProps = Props;
export type DropzoneRef = Ref;

const DropzoneBase: ForwardRefRenderFunction<Ref, Props> = (props, ref) => {
  const {
    children,
    onSelectFile,
    accept = { 'image/*': [] },
    preventClick = false,
    maxFiles = 1,
    whileDragActive = {},
    isDisabled,
    inputProps = {},
    ...rest
  } = props;

  const { getRootProps, getInputProps, isDragActive, open, inputRef } =
    useDropzone({
      onDrop: onSelectFile,
      accept,
      multiple: maxFiles > 1,
      maxFiles,
      noClick: preventClick,
      disabled: isDisabled,
    });

  useImperativeHandle(ref, () => ({
    openFileSelection() {
      inputRef.current?.removeAttribute('capture');

      open();
    },
    openCamera() {
      inputRef.current?.setAttribute('capture', 'user');

      open();
    },
  }));

  const resolveChildren = () => {
    if (typeof children === 'function') {
      return children({ isDragActive, openFileSelection: open });
    }

    return children;
  };

  const whileDragActiveProps = isDragActive ? whileDragActive : {};

  return (
    <Box ref={ref} {...getRootProps()} {...rest} {...whileDragActiveProps}>
      <input {...getInputProps()} {...inputProps} />

      {resolveChildren()}
    </Box>
  );
};

export const Dropzone = forwardRef(DropzoneBase);
